import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {useDispatch, useSelector} from 'react-redux';
import Command from '../../../state/actions/command';
import {Selector} from '../../../state/selector';
import {User} from '../../../state/types/User';
import {EmployeeRate} from '../../../state/types/Dashboard';
import {useEffect, useState} from 'react';
import Api from '../../../api';

interface EmployeeRateDialogProps {
    open: boolean;
    onClose: () => void;
}

const EmployeeRateDialog = (props: EmployeeRateDialogProps) => {
    const dispatch = useDispatch();
    const users = useSelector(Selector.User.users);
    const rates = useSelector(Selector.Dashboard.rates);
    const [editedRates, setEditedRates] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (props.open) {
            dispatch(Command.Dashboard.fetchEmployeeRateList({}));
        }
    }, [props.open]);

    useEffect(() => {
        const rateMap: Record<string, string> = {};
        rates.forEach((rate: EmployeeRate) => {
            rateMap[rate.userId] = (rate.hourlyRate / 100).toFixed(2);
        });
        setEditedRates(rateMap);
    }, [rates]);

    const userList: User[] = Object.values(users);

    const handleSaveAll = async () => {
        setSaving(true);
        try {
            for (const user of userList) {
                const value = parseFloat(editedRates[user.id] || '0');
                if (isNaN(value) || value < 0) {
                    continue;
                }
                await Api.Command.Rate.setEmployeeDefaultRate(user.id, Math.round(value * 100));
            }
            dispatch(Command.Dashboard.fetchEmployeeRateList({}));
            dispatch(Command.Notification.enqueueNotification({
                severity: 'success',
                message: 'Stundensätze wurden gespeichert.',
            }));
        } catch (e) {
            dispatch(Command.Notification.enqueueNotification({
                severity: 'error',
                message: 'Stundensätze konnten nicht gespeichert werden.',
            }));
        }
        setSaving(false);
    };

    return (
        <Dialog open={props.open} fullWidth={true} maxWidth={'md'} onClose={props.onClose}>
            <DialogTitle>Standard-Stundensätze verwalten</DialogTitle>
            <DialogContent>
                <TableContainer>
                    <Table size={'small'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Mitarbeiter</TableCell>
                                <TableCell>E-Mail</TableCell>
                                <TableCell align={'right'}>Stundensatz (EUR)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {userList.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>{user.firstName} {user.lastName}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell align={'right'}>
                                        <TextField
                                            size={'small'}
                                            type={'number'}
                                            value={editedRates[user.id] || ''}
                                            onChange={(e) => setEditedRates({
                                                ...editedRates,
                                                [user.id]: e.target.value,
                                            })}
                                            inputProps={{ min: 0, step: 0.01 }}
                                            sx={{ width: 120 }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>Abbrechen</Button>
                <Button
                    variant={'contained'}
                    startIcon={<SaveIcon />}
                    onClick={handleSaveAll}
                    disabled={saving}
                >
                    {saving ? 'Speichern...' : 'Speichern'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmployeeRateDialog;
